import { motion } from "framer-motion";
import { useState } from "react";

/* Client-firm logo marquee.
   Renders real logo images (bundled under /logos/) with a grayscale +
   opacity treatment so mixed brand palettes read as a coherent wall,
   then reveal full color on hover. If an image fails to load we fall
   back to a styled wordmark so the layout never breaks. */

const firms = [
    { name: "Richmond Vona",              slug: "richmond-vona",         src: "/logos/richmond-vona.webp",         h: 40 },
    { name: "Ricci Law Firm",              slug: "ricci",                 src: "/logos/ricci.png",                  h: 44 },
    { name: "Kwartler Manus",              slug: "kwartler-manus",        src: "/logos/kwartler-manus.svg",         h: 36 },
    { name: "LNN Law",                     slug: "lnn",                   src: "/logos/lnn.png",                    h: 42 },
    { name: "Peiffer Wolf",                slug: "peiffer-wolf",          src: "/logos/peiffer-wolf.png",           h: 36 },
    { name: "Simmons Hanly Conroy",        slug: "simmons-hanly-conroy",  src: "/logos/simmons-hanly-conroy.svg",   h: 32 },
    { name: "Sokolove Law",                slug: "sokolove",              src: "/logos/sokolove.png",               h: 34 },
    { name: "Jonathan Perkins Injury Lawyers", slug: "perkins",           src: "/logos/perkins.svg",                h: 40 },
    { name: "Laborde Earles",              slug: "laborde-earles",        src: "/logos/laborde-earles.svg",         h: 32 },
    { name: "Attorney Assistant",          slug: "attorney-assist",       src: "/logos/attorney-assist.webp",       h: 40 },
];

/* Fallback wordmark — used only if the logo image fails to load. */
const FallbackMark = ({ name }) => (
    <span className="font-display text-[var(--ink)] text-[18px] md:text-[20px] font-semibold tracking-[-0.01em] whitespace-nowrap uppercase">
        {name}
    </span>
);

const LogoTile = ({ firm }) => {
    const [failed, setFailed] = useState(false);
    return (
        <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.25 }}
            className="logo-tile flex-shrink-0 flex items-center"
            data-testid={`client-logo-${firm.slug}`}
        >
            {failed ? (
                <FallbackMark name={firm.name} />
            ) : (
                <img
                    src={firm.src}
                    alt={firm.name}
                    loading="lazy"
                    onError={() => setFailed(true)}
                    style={{ height: `${firm.h}px` }}
                    className="w-auto max-w-[220px] object-contain block"
                />
            )}
        </motion.div>
    );
};

const ClientLogosBar = () => {
    // Duplicate the list so the marquee can loop seamlessly.
    const loop = [...firms, ...firms];

    return (
        <section
            data-testid="client-logos-bar"
            className="relative py-10 md:py-12 bg-[var(--paper)] border-y border-[var(--border)]"
        >
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                <div className="flex flex-col md:flex-row items-center justify-center md:gap-3 mb-6">
                    <span className="hidden md:inline-block h-px w-12 bg-[var(--border-strong)]" />
                    <p className="font-mono text-[10.5px] tracking-[0.24em] text-[var(--muted)] uppercase text-center leading-[1.7]">
                        <span className="block md:inline">Trusted by law firms</span>
                        <span className="md:ml-1.5">across PI · mass tort · employment</span>
                    </p>
                    <span className="hidden md:inline-block h-px w-12 bg-[var(--border-strong)]" />
                </div>

                <div
                    className="relative overflow-hidden"
                    style={{
                        maskImage:
                            "linear-gradient(90deg, transparent, black 6%, black 94%, transparent)",
                        WebkitMaskImage:
                            "linear-gradient(90deg, transparent, black 6%, black 94%, transparent)",
                    }}
                >
                    <div className="flex client-marquee gap-12 md:gap-16 items-center whitespace-nowrap py-2">
                        {loop.map((firm, i) => (
                            <LogoTile key={`${firm.slug}-${i}`} firm={firm} />
                        ))}
                    </div>
                </div>
            </div>

            <style>{`
                .client-marquee {
                    animation: client-scroll 48s linear infinite;
                    will-change: transform;
                }
                .logo-tile {
                    filter: grayscale(1) contrast(1.05) brightness(0.92) drop-shadow(0 0 0.4px rgba(0,0,0,0.35));
                    opacity: 0.6;
                    transition: filter 0.35s ease, opacity 0.35s ease, transform 0.25s ease;
                }
                .logo-tile:hover {
                    filter: grayscale(0) contrast(1) brightness(1);
                    opacity: 1;
                }
                @keyframes client-scroll {
                    from { transform: translateX(0); }
                    to   { transform: translateX(-50%); }
                }
                @media (max-width: 767px) {
                    .client-marquee { animation-duration: 18s; }
                }
                @media (prefers-reduced-motion: reduce) {
                    .client-marquee { animation: none; }
                }
            `}</style>
        </section>
    );
};

export default ClientLogosBar;
